import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { timeout, catchError, map, share } from 'rxjs/operators';

import { ConfigService, SentryLogger, mapErrorForSentry } from '@fcom/core';
import { FinnairBoundItem, TimetableFlights } from '@fcom/dapi/api/models';
import { groupBy, isPresent, LocalDate } from '@fcom/core/utils';
import { TimetableService } from '@fcom/dapi/api/services';

import { DataState, SearchState } from '../interfaces/index';
import { getEarliestSearchDayInMonth } from '../utils/timetable.utils';

@Injectable()
export class TimetableSearchService {
  static readonly GET_TIMETABLE_TIMEOUT_MS = 20000;

  constructor(
    private configService: ConfigService,
    private sentryLogger: SentryLogger,
    private timetableService: TimetableService
  ) {}

  getTimetable(search: SearchState): Observable<DataState> {
    const departureDate: LocalDate = getEarliestSearchDayInMonth(search.selectedMonth);
    // fetch for requested date and remainder of month
    const numberOfDays: number =
      LocalDate.getAmountOfDaysInMonth(departureDate.localYear, departureDate.localMonth) - departureDate.localDay + 1;
    const params = {
      originLocationCode: search.origin.code,
      destinationLocationCode: search.destination.code,
      departureDate: departureDate.toString(),
      numberOfDays,
      directFlights: search.directOnly,
    };

    return this.timetableService.getTimetableForRoute$Response(this.configService.cfg.instantSearchUrl, params).pipe(
      timeout(TimetableSearchService.GET_TIMETABLE_TIMEOUT_MS),
      map((r: HttpResponse<any>) => r.body as TimetableFlights),
      catchError((err: unknown) => {
        this.sentryLogger.error('Error fetching timetable flights', {
          error: mapErrorForSentry(err),
        });

        return throwError(() => err);
      }),
      map((timetable) => {
        if (!isPresent(timetable)) {
          throw { code: 400, message: 'Malformed response' };
        }

        const bounds: FinnairBoundItem[] = timetable.flights;
        const flights = groupBy(bounds, (bound) =>
          bound.departure.dateTime.substring(0, bound.departure.dateTime.indexOf('T'))
        );

        return {
          availability: timetable.availability,
          flights,
          weeklyFlights: timetable.weeklyFlights,
        };
      }),
      share()
    );
  }
}
